import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { QrCode, Smartphone, ArrowRight, ChefHat, ReceiptEuro, BarChart3 } from "lucide-react";

const DEMO_SLUG = "comptoir-parisien";

const STATIONS = [
  { key: "kds", to: "/login?station=kds", icon: ChefHat, emoji: "🍳", title: "KDS Cuisine", desc: "Préparation des commandes", testid: "station-kds" },
  { key: "pos", to: "/login?station=pos", icon: ReceiptEuro, emoji: "🧾", title: "Caisse / POS", desc: "Tables, commandes et encaissements", testid: "station-pos" },
  { key: "manager", to: "/login?station=manager", icon: BarChart3, emoji: "📊", title: "Manager", desc: "Pilotage et statistiques", testid: "station-manager" },
];

export default function Landing() {
  const tables = [12, 5, 8];
  const [restos, setRestos] = useState([]);
  useEffect(() => { api.listPublicRestaurants().then(setRestos).catch(() => setRestos([])); }, []);
  return (
    <div className="min-h-screen bg-[#0C1713] text-stone-100 flex flex-col">
      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-14 sm:py-20">
        <div className="flex items-center mb-16">
          <img src="/novanext-logo.png" alt="NOVANEXT TABLE" data-testid="brand-logo" className="h-20 sm:h-24 w-auto rounded-xl" />
        </div>

        <div className="max-w-2xl">
          <p className="text-[#C69C4A] text-xs font-semibold uppercase tracking-[0.2em] mb-4">Commande à table · Démonstration MVP</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
            Le service en salle, plus simple en toute finesse.
          </h1>
          <p className="text-stone-400 text-base sm:text-lg leading-relaxed mb-10">
            Scannez, commandez, suivez. Une expérience fluide côté client et un poste de commande temps réel côté restaurant. Restaurant de démonstration : <span className="text-stone-100 font-semibold">Le Comptoir Parisien</span>.
          </p>
        </div>

        {/* Interface client — multi-restaurants */}
        <div className="rounded-3xl bg-[#111F1A] border border-[#1B3028] p-7 mb-10">
          <div className="w-11 h-11 rounded-xl bg-[#1A3D32] grid place-items-center mb-5">
            <Smartphone className="w-5 h-5 text-[#C69C4A]" />
          </div>
          <h2 className="font-serif-display text-2xl mb-2">Interface client</h2>
          <p className="text-stone-400 text-sm mb-6">Simule le scan du QR code d'une table. Choisissez un restaurant et une table (ouvrez sur votre téléphone).</p>
          <div className="space-y-5">
            {(restos.length ? restos : [{ slug: DEMO_SLUG, name: "Le Comptoir Parisien" }]).map((r) => (
              <div key={r.slug} data-testid={`demo-resto-${r.slug}`}>
                <p className="text-sm font-semibold text-stone-200 mb-2">{r.name}</p>
                <div className="flex flex-wrap gap-2.5">
                  {tables.map((t) => (
                    <Link key={t} data-testid={`demo-table-${r.slug}-${t}`} to={`/restaurant/${r.slug}/table/${t}`}
                      className="group inline-flex items-center gap-2 rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold px-5 py-2.5 text-sm transition-colors">
                      <QrCode className="w-4 h-4" /> Table {t}
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Poste restaurant — 3 espaces */}
        <p className="text-[#C69C4A] text-xs font-semibold uppercase tracking-[0.2em] mb-4">Poste restaurant · Choisissez votre espace</p>
        <div className="grid sm:grid-cols-3 gap-5">
          {STATIONS.map((s) => (
            <Link key={s.key} data-testid={s.testid} to={s.to}
              className="group rounded-3xl bg-[#111F1A] border border-[#1B3028] hover:border-[#C69C4A] p-7 transition-colors flex flex-col">
              <div className="text-4xl mb-4">{s.emoji}</div>
              <h2 className="font-serif-display text-2xl mb-1.5 flex items-center gap-2"><s.icon className="w-5 h-5 text-[#C69C4A]" /> {s.title}</h2>
              <p className="text-stone-400 text-sm flex-1">{s.desc}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-[#C69C4A] font-semibold text-sm">
                Ouvrir <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-10 rounded-2xl bg-[#0A1712] border border-[#1B3028] p-5 text-sm text-stone-400">
          <span className="text-[#C69C4A] font-semibold">Une seule application, un seul backend NOVANEXT :</span> KDS, Caisse/POS et Manager partagent les mêmes commandes en temps réel. Installable en PWA sur un écran cuisine, un terminal de caisse ou l'ordinateur du responsable.
        </div>
      </div>
      <footer className="text-center py-6 text-xs text-stone-500 border-t border-[#1B3028]">Powered by NOVANEXT</footer>
    </div>
  );
}
